import DOMPurify from 'dompurify';
import type { PublicInventoryItem } from '../services/inventoryItemPublicService';

/**
 * QR-LANDING-TEMPLATE.1 — safe client-side rendering of a venue's QR landing
 * template.
 *
 * Two safety layers protect the rendered output:
 *   1. renderQrLandingTemplate substitutes {{variable}} slots and HTML-ESCAPES
 *      every value. There is no triple-stash / raw output — a variable can never
 *      introduce markup.
 *   2. sanitizeQrLandingHtml runs the final HTML through DOMPurify, which strips
 *      any scripts, event handlers, iframes, etc. that a template author might
 *      have written directly. (The backend also rejects those on save, so this
 *      is defence-in-depth.)
 *
 * Only the variables listed in QR_LANDING_VARIABLES are supported; unknown
 * {{tokens}} render as empty strings.
 */

export interface QrLandingVariable {
  /** Token name used as {{name}} in a template. */
  name: string;
  /** Human label shown in the admin editor's variable helper list. */
  label: string;
}

/** The canonical supported variable list (documented for the editor + docs). */
export const QR_LANDING_VARIABLES: QrLandingVariable[] = [
  // Cleaner, non-duplicating variables (preferred for new templates).
  { name: 'primaryTitle', label: 'Primary title (wine name incl. vintage, no size)' },
  { name: 'subtitle', label: 'Subtitle (producer)' },
  { name: 'regionPath', label: 'Region path (country → region)' },
  { name: 'price', label: 'Formatted price' },
  { name: 'format', label: 'Format / size' },
  { name: 'hasVenueLogo', label: 'Has venue logo (for {{#if}})' },
  // Original variables (kept for backward compatibility).
  { name: 'venueName', label: 'Venue name' },
  { name: 'venueLogoUrl', label: 'Venue logo URL' },
  { name: 'displayName', label: 'Item display name' },
  { name: 'wineName', label: 'Wine name' },
  { name: 'vintage', label: 'Vintage' },
  { name: 'producer', label: 'Producer' },
  { name: 'region', label: 'Region' },
  { name: 'country', label: 'Country' },
  { name: 'color', label: 'Wine colour' },
  { name: 'size', label: 'Size (value + unit)' },
  { name: 'shortId', label: 'Short ID' },
];

export type QrLandingVarValues = Record<string, string>;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const titleCase = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

/** Format a minor-unit price (cents) into a customer-facing currency string. */
const formatPrice = (minor: number | null, currency: string | null): string => {
  if (minor == null) return '';
  const major = minor / 100;
  const code = currency || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).format(major);
  } catch {
    return `${major.toFixed(2)} ${code}`.trim();
  }
};

/** Build the variable value map from a public inventory item payload. */
export function buildQrLandingVars(item: PublicInventoryItem): QrLandingVarValues {
  const size =
    item.sizeValue != null && item.sizeUnit ? `${item.sizeValue} ${item.sizeUnit}` : '';
  const logoUrl = item.venue?.logoUrl ?? '';
  // primaryTitle = wine name (already carries vintage, no size). Falls back to
  // the item display name for non-wine items. Deliberately NOT displayName for
  // wines, since displayName appends the size and would duplicate it.
  const primaryTitle = item.wine?.wineName || item.displayName || '';
  return {
    // Cleaner variables.
    primaryTitle,
    subtitle: item.wine?.producer ?? '',
    regionPath: item.wine?.regionPath ?? '',
    price: formatPrice(item.salePrice, item.salePriceCurrency),
    format: size,
    hasVenueLogo: logoUrl ? 'true' : '',
    // Original variables.
    venueName: item.venue?.name ?? '',
    venueLogoUrl: logoUrl,
    displayName: item.displayName ?? '',
    wineName: item.wine?.wineName ?? '',
    vintage: item.wine?.displayVintage ?? '',
    producer: item.wine?.producer ?? '',
    region: item.wine?.region ?? '',
    country: item.wine?.country ?? '',
    color: item.wine?.color ? titleCase(item.wine.color) : '',
    size,
    shortId: item.shortId ?? '',
  };
}

/**
 * Minimal, safe conditional sections so templates can degrade gracefully:
 *   {{#if var}}…{{/if}}       — kept when var is a non-empty string
 *   {{#unless var}}…{{/unless}} — kept when var is empty/missing
 * Non-nested, evaluated before variable substitution. This touches only the
 * renderer (not the sanitizer): the output is still escaped + DOMPurify'd, so
 * conditionals cannot introduce active content.
 */
function applyConditionals(html: string, vars: QrLandingVarValues): string {
  const truthy = (name: string) => Boolean(vars[name]);
  let out = html.replace(
    /\{\{#if\s+([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_m, name: string, body: string) => (truthy(name) ? body : ''),
  );
  out = out.replace(
    /\{\{#unless\s+([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/unless\}\}/g,
    (_m, name: string, body: string) => (truthy(name) ? '' : body),
  );
  return out;
}

/**
 * Resolve {{#if}}/{{#unless}} sections, then substitute {{variable}} slots with
 * HTML-escaped values. Unknown tokens become empty strings. No raw/triple-stash
 * output is supported by design.
 */
export function renderQrLandingTemplate(templateHtml: string, vars: QrLandingVarValues): string {
  return applyConditionals(templateHtml, vars).replace(
    /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
    (_match, name: string) => escapeHtml(vars[name] ?? ''),
  );
}

/** Final sanitization pass before the HTML is injected into the DOM. */
export function sanitizeQrLandingHtml(html: string): string {
  // FORCE_BODY keeps a leading <style> block in the fragment (without it the
  // parser hoists <style> into <head> and DOMPurify drops it, leaving the page
  // unstyled). Scripts, event handlers, iframes and unsafe URIs are still
  // stripped by the html profile.
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, FORCE_BODY: true });
}

/** Convenience: render a template with an item's values, then sanitize. */
export function renderAndSanitizeQrLanding(
  templateHtml: string,
  item: PublicInventoryItem,
): string {
  return sanitizeQrLandingHtml(renderQrLandingTemplate(templateHtml, buildQrLandingVars(item)));
}

/**
 * General Provisions QR landing starter template — navy + gold luxury cellar
 * look, inspired by the GP "Wine Cellar" reference design.
 *
 * Venue-scoping: this is only the string loaded by the editor's "Insert starter
 * template" button. It changes nothing until an operator saves it for a
 * specific venue, and it is unrelated to the default fallback page. Other
 * venues are unaffected. It uses {{venueName}}/{{venueLogoUrl}} so it adapts to
 * whichever venue adopts it.
 *
 * Design notes:
 *   - Logo lockup at top: {{#if venueLogoUrl}} shows the image; otherwise a gold
 *     "The Cellar" kicker + {{venueName}} wordmark stands in for it.
 *   - {{primaryTitle}} used once (wine name incl. vintage) — no repeated
 *     vintage/size/name. Size shows only as a chip.
 *   - Every metadata field (subtitle, region, price, colour, format) is wrapped
 *     in {{#if}} so missing data collapses cleanly with no empty rows.
 *   - HTML/CSS only, sanitizer-compatible, mobile-first for narrow iPhones.
 */
export const GENERAL_PROVISIONS_STARTER_TEMPLATE = `<style>
  .gp-page {
    min-height: 100vh;
    margin: 0;
    background: radial-gradient(circle at 50% -8%, #17222f 0%, #0d151e 55%, #080d13 100%);
    color: #f2ece1;
    font-family: "Cormorant Garamond", "EB Garamond", Georgia, "Times New Roman", serif;
    display: flex;
    justify-content: center;
  }
  .gp-card {
    width: 100%;
    max-width: 440px;
    padding: 52px 28px 56px;
    text-align: center;
    box-sizing: border-box;
  }
  /* Logo / brand lockup */
  .gp-logo { max-width: 158px; max-height: 92px; object-fit: contain; margin: 0 auto 4px; display: block; }
  .gp-brand-est { font-family: system-ui, sans-serif; font-size: 0.54rem; letter-spacing: 0.36em; text-transform: uppercase; color: #c3a45e; margin: 0 0 10px; }
  .gp-brand-name { font-size: 1.42rem; letter-spacing: 0.1em; text-transform: uppercase; color: #e9e0cf; margin: 0; line-height: 1.15; }
  .gp-rule { width: 46px; height: 1px; background: #c3a45e; opacity: 0.75; margin: 22px auto 28px; }
  /* Wine */
  .gp-kicker { font-family: system-ui, sans-serif; font-size: 0.6rem; letter-spacing: 0.38em; text-transform: uppercase; color: #c3a45e; margin: 0 0 14px; }
  .gp-title { font-size: 2.2rem; line-height: 1.16; margin: 0 0 10px; color: #f6f1e7; font-weight: 500; }
  .gp-subtitle { font-size: 1.16rem; font-style: italic; color: #cabca2; margin: 0 0 18px; }
  .gp-origin { font-family: system-ui, sans-serif; font-size: 0.86rem; letter-spacing: 0.03em; color: #8b93a1; margin: 0 0 4px; }
  .gp-price { font-size: 1.7rem; color: #e7d5a8; margin: 28px 0 24px; font-weight: 500; }
  .gp-chips { display: flex; gap: 9px; justify-content: center; flex-wrap: wrap; }
  .gp-chip {
    font-family: system-ui, sans-serif; font-size: 0.62rem; letter-spacing: 0.16em; text-transform: uppercase;
    color: #d9c9a6; border: 1px solid rgba(195,164,94,0.4); border-radius: 999px; padding: 7px 15px;
  }
  .gp-id { font-family: monospace; font-size: 0.6rem; letter-spacing: 0.16em; color: #4f5866; margin-top: 44px; }
</style>
<div class="gp-page">
  <div class="gp-card">
    {{#if venueLogoUrl}}<img class="gp-logo" src="{{venueLogoUrl}}" alt="{{venueName}}" />{{/if}}
    {{#unless venueLogoUrl}}
    <p class="gp-brand-est">The Cellar</p>
    <p class="gp-brand-name">{{venueName}}</p>
    {{/unless}}

    <div class="gp-rule"></div>

    <p class="gp-kicker">The Collection</p>
    <h1 class="gp-title">{{primaryTitle}}</h1>
    {{#if subtitle}}<p class="gp-subtitle">{{subtitle}}</p>{{/if}}

    {{#if regionPath}}<p class="gp-origin">{{regionPath}}</p>{{/if}}
    {{#unless regionPath}}{{#if country}}<p class="gp-origin">{{country}}</p>{{/if}}{{/unless}}

    {{#if price}}<p class="gp-price">{{price}}</p>{{/if}}

    <div class="gp-chips">
      {{#if color}}<span class="gp-chip">{{color}}</span>{{/if}}
      {{#if format}}<span class="gp-chip">{{format}}</span>{{/if}}
    </div>

    <p class="gp-id">{{shortId}}</p>
  </div>
</div>`;
